import React, { useEffect, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import OtpBoxes from './OtpBoxes';
import ClearInputButton from './ClearInputButton';
import { useSlowFlag, useLookupTicket, manualReasonText } from './slowLookup';
import { registrationService, getErrorMessage, getErrorCode, getErrorDetails } from '../../../services/apiService';

const UDYAM_RE = /^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/;
const normalizeUan = (v) => String(v || '').toUpperCase().replace(/\s+/g, '');

const Chip = ({ icon, children, tone = 'neutral' }) => {
  const tones = {
    neutral: 'border-border bg-muted/40 text-text-secondary',
    success: 'border-success/30 bg-success/5 text-success',
    warning: 'border-warning/40 bg-warning/10 text-warning',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>
      {icon && <Icon name={icon} size={12} />}
      {children}
    </span>
  );
};

const inr = (n) => (typeof n === 'number' ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n) : null);

/** Optional: one OTP to the Udyam-registered mobile unlocks the exact turnover. */
const UdyamOtpCard = ({ sessionId, identity, onResult }) => {
  const exact = identity?.udyam?.exact;
  const [mobile, setMobile] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | sending | sent | verifying
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [resendAt, setResendAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (phase !== 'sent') return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [phase]);

  if (exact && typeof exact.total_turnover === 'number') {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-success/30 bg-success/5 px-4 py-3" role="status">
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-success text-success-foreground">
          <Icon name="Check" size={18} strokeWidth={3} />
        </span>
        <div>
          <p className="text-sm font-semibold text-foreground">Exact turnover confirmed via Udyam OTP</p>
          <p className="text-xs text-text-secondary">{inr(exact.total_turnover)} {exact.turnover_fy ? `for FY ${exact.turnover_fy}` : ''} — locked in the turnover field above.</p>
        </div>
      </div>
    );
  }

  const send = async () => {
    setPhase('sending');
    setError(null);
    try {
      const res = await registrationService.sendUdyamOtp(sessionId, mobile);
      setResendAt(new Date(res.data.resend_available_at).getTime());
      setAttemptsLeft(res.data.attempts_remaining ?? null);
      setCode('');
      setPhase('sent');
    } catch (e) {
      setError(getErrorMessage(e));
      setPhase('idle');
    }
  };
  const verify = async () => {
    if (code.length < 4) return;
    setPhase('verifying');
    setError(null);
    try {
      const res = await registrationService.verifyUdyamOtp(sessionId, code);
      onResult?.(res.data);
    } catch (e) {
      setError(getErrorMessage(e));
      const d = getErrorDetails(e);
      if (d?.attempts_remaining?.[0] !== undefined) setAttemptsLeft(Number(d.attempts_remaining[0]));
      setCode('');
      setPhase(getErrorCode(e) === 'KYC_ATTEMPTS_EXCEEDED' ? 'idle' : 'sent');
    }
  };
  const resendIn = resendAt ? Math.max(0, Math.ceil((resendAt - now) / 1000)) : 0;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-between rounded-lg border border-dashed border-primary/40 px-4 py-3 text-left hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <span>
          <span className="flex items-center gap-2 text-sm font-semibold text-foreground"><Icon name="Sparkles" size={16} className="text-primary" /> Get your exact turnover with one OTP</span>
          <span className="mt-0.5 block text-xs text-text-secondary">Optional. A code goes to the mobile registered on Udyam; we fill the exact figure instead of the GST slab.</span>
        </span>
        <Icon name="ChevronRight" size={18} className="text-text-secondary" />
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-white p-4 animate-rise-in motion-reduce:animate-none">
      <p className="text-sm font-semibold text-foreground">Udyam OTP — exact turnover</p>
      <p className="text-xs text-text-secondary">
        Udyam will text a code to the mobile registered there{identity?.udyam?.mobile_masked ? ` (${identity.udyam.mobile_masked})` : ''}. Optional — skip it and the GST slab stays.
      </p>
      {phase !== 'sent' && phase !== 'verifying' ? (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
          <label className="flex-1 text-xs text-text-secondary">
            Udyam-registered mobile
            <input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} inputMode="numeric" placeholder="10-digit mobile" className="mt-1 flex h-10 w-full rounded-md border border-border bg-white px-3 font-mono text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" />
          </label>
          <Button variant="default" onClick={send} loading={phase === 'sending'} disabled={!/^[6-9]\d{9}$/.test(mobile) || phase === 'sending'} iconName="Send" iconPosition="right">Send code</Button>
          <button type="button" onClick={() => setOpen(false)} className="text-xs text-text-secondary hover:underline sm:ml-2">Skip</button>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <OtpBoxes value={code} onChange={setCode} onSubmit={verify} disabled={phase === 'verifying'} error={Boolean(error)} autoFocus />
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="default" onClick={verify} loading={phase === 'verifying'} disabled={code.length < 4 || phase === 'verifying'} iconName="ShieldCheck" iconPosition="right">Verify</Button>
            <button type="button" onClick={send} disabled={resendIn > 0} className="text-xs text-primary hover:underline disabled:text-text-secondary disabled:no-underline">
              {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
            </button>
            {attemptsLeft !== null && <span className="text-xs text-text-secondary">{attemptsLeft} attempt{attemptsLeft === 1 ? '' : 's'} left</span>}
          </div>
        </div>
      )}
      {error && <p className="mt-2 flex items-center gap-2 text-sm text-destructive" role="alert"><Icon name="AlertCircle" size={16} /> {error}</p>}
    </div>
  );
};

/**
 * Udyam lookup for MSME-registered vendors: the number (often already found from the GST mobile)
 * → Surepass udyog-aadhaar → category, classification, activity and the certificate, locked.
 *
 * When the portal cannot help — down, timed out, unreachable, or still working after 1.5 s — the
 * vendor may keep the number as typed and fill the category, dates and certificate themselves; the
 * server records why, and the reviewer verifies the registration before approval. "Change number"
 * undoes a lookup (a digit was wrong, the wrong enterprise came back) and puts the number back in
 * the box to correct.
 */
const UdyamLookupPanel = ({ sessionId, identity, kyc, formData, onResult, uploadedFiles, onPreviewDocument }) => {
  const udyam = identity?.udyam;
  const verified = Boolean(identity?.udyam_verified_at && udyam);
  // The portal could not help and the vendor went on with the number as typed; a later lookup clears this.
  const manual = !verified && Boolean(identity?.udyam_manual);
  const hint = identity?.msme_hint?.number || null;
  const [uan, setUan] = useState(normalizeUan(formData.msmeNumber || identity?.udyam_manual?.uan || hint || ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  // The server said the portal did not answer, so the vendor may continue by hand.
  const [manualAllowed, setManualAllowed] = useState(false);
  const [continuing, setContinuing] = useState(false);
  // "Try looking up again" from the kept-as-entered card shows the form once more.
  const [retrying, setRetrying] = useState(false);
  const [confirmChange, setConfirmChange] = useState(false);
  const [changing, setChanging] = useState(false);
  const lookup = useLookupTicket();
  const slow = useSlowFlag(busy);

  useEffect(() => {
    if (!uan && (formData.msmeNumber || hint)) setUan(normalizeUan(formData.msmeNumber || hint));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.msmeNumber, hint]);

  const doLookup = async () => {
    if (!UDYAM_RE.test(uan)) return;
    const ticket = lookup.start();
    setBusy(true);
    setError(null);
    setManualAllowed(false);
    try {
      const res = await registrationService.lookupUdyam(sessionId, uan);
      if (!lookup.isCurrent(ticket)) return; // the vendor went on by hand meanwhile
      setWarnings(res.data?.warnings || []);
      setRetrying(false);
      onResult?.(res.data);
    } catch (e) {
      if (!lookup.isCurrent(ticket)) return;
      setError(getErrorMessage(e));
      setManualAllowed(getErrorDetails(e)?.manual_allowed?.[0] === 'true');
    } finally {
      if (lookup.isCurrent(ticket)) setBusy(false);
    }
  };

  /** Keep the number as typed — after the portal failed, or while it is slow. */
  const continueManually = async () => {
    if (continuing || !UDYAM_RE.test(uan)) return;
    setContinuing(true);
    const walkingAway = busy;
    try {
      const res = await registrationService.udyamManual(sessionId, uan);
      if (res?.success && res.data) {
        if (walkingAway) lookup.abandon();
        setBusy(false);
        setError(null);
        setManualAllowed(false);
        setRetrying(false);
        onResult?.(res.data);
      } else {
        throw new Error(res?.message || 'Could not continue');
      }
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setContinuing(false);
    }
  };

  /** "Change number": undo the lookup (or the kept number) on the server; the number stays in the box. */
  const changeNumber = async () => {
    setChanging(true);
    try {
      const res = await registrationService.clearUdyam(sessionId);
      setConfirmChange(false);
      setRetrying(false);
      setWarnings([]);
      setError(null);
      setManualAllowed(false);
      const kept = res?.data?.steps?.step4?.msmeNumber;
      if (kept) setUan(normalizeUan(kept));
      onResult?.(res?.data);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setChanging(false);
    }
  };

  if (kyc?.enabled === false || kyc?.features?.udyam === 'unavailable') return null;

  const changeButton = (
    <button
      type="button"
      onClick={() => setConfirmChange(true)}
      aria-label="Change Udyam number"
      title="Change Udyam number"
      data-testid="udyam-change"
      className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-border bg-white px-2.5 py-1 text-xs font-medium text-text-secondary hover:border-destructive/40 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <Icon name="X" size={12} /> Change number
    </button>
  );
  const changeControl = confirmChange ? (
    <div className="mt-3 rounded-md border border-border bg-white px-3 py-2" data-testid="udyam-change-confirm">
      <p className="text-xs text-foreground">
        This removes the MSME details {manual ? 'kept for this number' : 'and certificate fetched from the Udyam portal'} so you can correct the number. Continue?
      </p>
      <div className="mt-2 flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setConfirmChange(false)} disabled={changing}>Keep it</Button>
        <Button variant="destructive" size="sm" iconName="X" iconPosition="left" onClick={changeNumber} loading={changing} disabled={changing}>
          Yes, change number
        </Button>
      </div>
    </div>
  ) : null;

  // ---------- kept as entered (the portal could not help) ----------
  if (manual && !retrying && !busy) {
    return (
      <div className="rounded-xl border border-warning/40 bg-warning/5 p-4 animate-rise-in motion-reduce:animate-none" role="status" aria-live="polite" data-testid="udyam-manual-card">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-warning/15 text-warning">
            <Icon name="ShieldAlert" size={20} strokeWidth={2.5} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-warning">Udyam number kept as entered — not verified yet</p>
              {changeButton}
            </div>
            <p className="mt-0.5 font-mono text-lg font-semibold tracking-[0.08em] text-foreground">{identity.udyam_manual?.uan || uan}</p>
            <p className="mt-1.5 text-sm text-text-secondary">
              You continued because {manualReasonText(identity.udyam_manual?.reason)}. Fill the MSME category and dates below and upload your
              Udyam certificate — Amber will verify the registration before approval.
            </p>
            <div className="mt-3">
              <Button
                variant="outline"
                size="sm"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={() => {
                  setRetrying(true);
                  setError(null);
                }}
              >
                Try looking up again
              </Button>
            </div>
            {changeControl}
            {error && <p className="mt-2 flex items-center gap-2 text-sm text-destructive" role="alert"><Icon name="AlertCircle" size={16} /> {error}</p>}
          </div>
        </div>
      </div>
    );
  }

  if (verified) {
    const cert = uploadedFiles?.MSME_CERTIFICATE;
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-success/30 bg-success/5 p-4 animate-rise-in motion-reduce:animate-none" role="status" aria-live="polite">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-success text-success-foreground animate-otp-pop motion-reduce:animate-none">
              <Icon name="Factory" size={20} strokeWidth={2.5} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-success">Udyam registration found</p>
                {changeButton}
              </div>
              <p className="mt-0.5 text-lg font-semibold leading-tight text-foreground">{udyam.enterprise_name || 'Enterprise found'}</p>
              <p className="mt-1 font-mono text-sm text-text-secondary">{udyam.uan}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {udyam.category && <Chip icon="Layers" tone="success">{udyam.category.charAt(0).toUpperCase() + udyam.category.slice(1)} enterprise</Chip>}
                {udyam.classification_year && <Chip icon="CalendarCheck">Classified {udyam.classification_year}{udyam.classification_date ? ` · ${udyam.classification_date}` : ''}</Chip>}
                {udyam.major_activity && <Chip icon="Wrench">{udyam.major_activity.charAt(0).toUpperCase() + udyam.major_activity.slice(1)}</Chip>}
                {udyam.name_match === false && <Chip icon="AlertTriangle" tone="warning">Name differs from GST legal name</Chip>}
                {cert && <Chip icon="FileCheck" tone="success">Certificate fetched from Udyam</Chip>}
              </div>
              {warnings.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-text-secondary">
                  {warnings.map((w) => (<li key={w.code} className="flex items-start gap-1.5"><Icon name="Info" size={12} className="mt-0.5 text-warning" />{w.message}</li>))}
                </ul>
              )}
              {cert && onPreviewDocument && (
                <button type="button" onClick={() => onPreviewDocument(cert, 'MSME_CERTIFICATE')} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                  <Icon name="Eye" size={12} /> Preview certificate ({cert.file_name})
                </button>
              )}
              {changeControl}
              {error && <p className="mt-2 flex items-center gap-2 text-sm text-destructive" role="alert"><Icon name="AlertCircle" size={16} /> {error}</p>}
            </div>
          </div>
        </div>
        {kyc?.features?.udyam_otp !== 'unavailable' && <UdyamOtpCard sessionId={sessionId} identity={identity} onResult={onResult} />}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-primary/30 bg-white p-4 shadow-sm animate-rise-in motion-reduce:animate-none">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Icon name="Factory" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Look up your Udyam registration</p>
          <p className="text-xs text-text-secondary">
            {hint && normalizeUan(hint) === uan
              ? 'Found from your GST-registered mobile — confirm it is yours and look it up.'
              : 'We fill the MSME category, classification and certificate straight from the Udyam portal.'}
          </p>
        </div>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto] md:items-start">
        <div className="space-y-2">
          <label htmlFor="udyam-input" className="text-sm font-medium leading-none text-foreground">Udyam Registration Number <span className="ml-1 text-destructive">*</span></label>
          <div className="relative">
            <input
              id="udyam-input"
              value={uan}
              onChange={(e) => { setUan(normalizeUan(e.target.value).slice(0, 19)); setError(null); setManualAllowed(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') doLookup(); }}
              placeholder="UDYAM-MH-18-0000001"
              maxLength={19}
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              className={`flex h-11 w-full rounded-md border bg-white px-3 pr-10 font-mono text-base tracking-[0.08em] text-foreground shadow-sm placeholder:tracking-normal placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60 ${
                uan && !UDYAM_RE.test(uan) && uan.length >= 19 ? 'border-destructive' : UDYAM_RE.test(uan) ? 'border-success/50' : 'border-border'
              }`}
            />
            <ClearInputButton
              visible={Boolean(uan) && !busy}
              label="Clear Udyam number"
              testId="udyam-clear-input"
              onClear={() => {
                setUan('');
                setError(null);
                setManualAllowed(false);
                document.getElementById('udyam-input')?.focus();
              }}
            />
          </div>
          <p className="text-xs text-text-secondary">Format: UDYAM-XX-00-0000000 (on your Udyam certificate)</p>
        </div>
        <Button variant="default" onClick={doLookup} loading={busy} disabled={!UDYAM_RE.test(uan) || busy} iconName="Search" iconPosition="right" className="h-11 md:mt-[26px]">
          Look up
        </Button>
      </div>

      {/* The portal is slow: the vendor may stop waiting and go on with the number as typed. */}
      {busy && slow && (
        <div className="mt-3 rounded-md border border-warning/40 bg-warning/5 px-3 py-2.5" role="status" data-testid="udyam-slow-offer">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Icon name="Hourglass" size={16} className="text-warning" /> The Udyam portal is taking longer than usual.
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            Keep waiting, or continue with this number and fill in the MSME details yourself — Amber verifies them before approval.
          </p>
          <Button variant="outline" size="sm" className="mt-2" iconName="PencilLine" iconPosition="left" loading={continuing} disabled={continuing} onClick={continueManually}>
            Enter details manually
          </Button>
        </div>
      )}

      {error && (
        <div className="mt-3" role="alert">
          <p className="flex items-center gap-2 text-sm text-destructive"><Icon name="AlertCircle" size={16} /> {error}</p>
          {manualAllowed && (
            <div className="mt-2 rounded-md border border-warning/40 bg-white px-3 py-2" data-testid="udyam-manual-offer">
              <p className="text-xs text-text-secondary">
                You can continue with this number and enter the MSME category, dates and certificate yourself — Amber will verify them before approval.
              </p>
              <Button variant="outline" size="sm" className="mt-2" iconName="PencilLine" iconPosition="left" loading={continuing} disabled={continuing || !UDYAM_RE.test(uan)} onClick={continueManually}>
                Enter details manually
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UdyamLookupPanel;
